import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { clientService } from '../../services/clientService';
import { typesService } from '../../services/typesService';
import { Client } from '../../types/client';
import {
  ConfigTemplate,
  ConfigTemplateAssignResult,
  isAnepTemplate,
} from '../../types/configTemplate';

interface Props {
  isOpen: boolean;
  template: ConfigTemplate;
  onClose: () => void;
  /** Called after a successful assignment so the caller can refresh counts. */
  onAssigned: (result: ConfigTemplateAssignResult) => void;
}

type ImeiStatus = 'ok' | 'already' | 'other_client' | 'unknown' | 'wrong_type';

/** Split a pasted list of IMEIs on newlines, commas, semicolons or spaces. */
const parseDeviceIds = (raw: string): string[] =>
  Array.from(new Set(raw.split(/[\s,;]+/).map((s) => s.trim().toUpperCase()).filter(Boolean)));

/** What the API said about each typed IMEI. */
const classify = (ids: string[], check: ConfigTemplateAssignResult): Record<string, ImeiStatus> => {
  const out: Record<string, ImeiStatus> = {};
  const other = new Set(check.other_client_devices);
  const unknown = new Set(check.unknown_devices);
  const wrong = new Set(check.wrong_type_devices);
  for (const id of ids) {
    if (other.has(id)) out[id] = 'other_client';
    else if (unknown.has(id)) out[id] = 'unknown';
    else if (wrong.has(id)) out[id] = 'wrong_type';
    else out[id] = 'ok';
  }
  return out;
};

/**
 * Give the template to a client's devices. Two cases:
 *
 *  - the whole client: every active device of the template's gateway type
 *    owned by the client. A dry run tells, before anything is written, how
 *    many devices that is and how many already use the template;
 *  - some devices: the IMEIs typed in. They are checked as they are typed
 *    (a dry run on the API): each must belong to the client, be known and
 *    be of the right gateway type. Nothing is sent while one is wrong.
 *
 * In both cases the devices take this template in place of the one they
 * had; their own overrides are kept unless asked otherwise.
 */
export const AssignTemplateModal: React.FC<Props> = ({ isOpen, template, onClose, onAssigned }) => {
  const { formatNumber, t } = useI18n();
  const anep = isAnepTemplate(template.client_id);
  const [clients, setClients] = useState<Client[]>([]);
  const [clientId, setClientId] = useState('');
  const [mode, setMode] = useState<'all' | 'some'>('all');
  const [deviceText, setDeviceText] = useState('');
  const [clearOverrides, setClearOverrides] = useState(false);
  const [check, setCheck] = useState<ConfigTemplateAssignResult | null>(null);
  const [checking, setChecking] = useState(false);
  // Dry run for the "whole client" case: how many devices it would touch.
  const [preview, setPreview] = useState<ConfigTemplateAssignResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ConfigTemplateAssignResult | null>(null);
  const checkSeq = useRef(0);
  const previewSeq = useRef(0);

  useEffect(() => {
    if (!isOpen) return;
    setClientId(anep ? '' : template.client_id);
    setMode('all');
    setDeviceText('');
    setClearOverrides(false);
    setCheck(null);
    setPreview(null);
    setError(null);
    setResult(null);
    let cancelled = false;
    clientService
      .fetchActiveClients()
      .then((list) => {
        if (!cancelled) setClients(list);
      })
      .catch((err) => {
        if (!cancelled) console.error('[ASSIGN TEMPLATE] fetch clients:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, anep, template.client_id]);

  const clientName = useMemo(() => {
    const c = clients.find((x) => x.id === clientId);
    return c?.name ?? clientId;
  }, [clients, clientId]);

  const deviceIds = useMemo(() => parseDeviceIds(deviceText), [deviceText]);

  // Check the typed IMEIs against the API, debounced, as a dry run.
  const runCheck = useCallback(async (ids: string[], cid: string) => {
    const seq = ++checkSeq.current;
    if (!ids.length || !cid) {
      setCheck(null);
      setChecking(false);
      return;
    }
    setChecking(true);
    try {
      const res = await typesService.assignConfigTemplate(Number(template.id), {
        device_ids: ids,
        expect_client_id: cid,
        replace_existing_template: true,
        clear_overrides: false,
        dry_run: true,
      });
      if (seq === checkSeq.current) setCheck(res);
    } catch (err: any) {
      if (seq === checkSeq.current) setError(err.response?.data?.detail || err.message || t('configTemplates.assign.checkFailed'));
    } finally {
      if (seq === checkSeq.current) setChecking(false);
    }
  }, [template.id, t]);

  useEffect(() => {
    if (!isOpen || mode !== 'some') return;
    const handle = setTimeout(() => runCheck(deviceIds, clientId), 400);
    return () => clearTimeout(handle);
  }, [isOpen, mode, deviceIds, clientId, runCheck]);

  // Count the client's devices (dry run) as soon as a client is picked.
  useEffect(() => {
    if (!isOpen || !clientId) {
      setPreview(null);
      return;
    }
    const seq = ++previewSeq.current;
    let cancelled = false;
    setPreviewing(true);
    typesService
      .assignConfigTemplate(Number(template.id), {
        client_ids: [clientId],
        replace_existing_template: true,
        clear_overrides: false,
        dry_run: true,
      })
      .then((res) => { if (!cancelled && seq === previewSeq.current) setPreview(res); })
      .catch((err) => { if (!cancelled) console.warn('[ASSIGN TEMPLATE] preview failed:', err?.message || err); })
      .finally(() => { if (!cancelled && seq === previewSeq.current) setPreviewing(false); });
    return () => { cancelled = true; };
  }, [isOpen, clientId, template.id]);

  const statuses = useMemo(
    () => (check && mode === 'some' ? classify(deviceIds, check) : {}),
    [check, deviceIds, mode],
  );
  const invalidIds = useMemo(
    () => deviceIds.filter((id) => statuses[id] && statuses[id] !== 'ok'),
    [deviceIds, statuses],
  );
  const readyCount = deviceIds.length - invalidIds.length;
  const checkedAll = check !== null && !checking && deviceIds.length > 0;
  const canSubmit =
    !submitting && !!clientId &&
    (mode === 'all'
      ? Boolean(preview && preview.matched > 0)
      : (deviceIds.length > 0 && checkedAll && invalidIds.length === 0));

  const removeInvalid = () => {
    const keep = deviceIds.filter((id) => !invalidIds.includes(id));
    setDeviceText(keep.join('\n'));
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await typesService.assignConfigTemplate(Number(template.id), {
        client_ids: mode === 'all' ? [clientId] : undefined,
        device_ids: mode === 'some' ? deviceIds : undefined,
        expect_client_id: mode === 'some' ? clientId : undefined,
        replace_existing_template: true,
        clear_overrides: clearOverrides,
      });
      setResult(res);
      onAssigned(res);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || t('configTemplates.assign.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const statusLabel: Record<ImeiStatus, string> = {
    ok: t('configTemplates.assign.statusOk'),
    already: t('configTemplates.assign.statusAlready'),
    other_client: t('configTemplates.assign.statusOtherClient'),
    unknown: t('configTemplates.assign.statusUnknown'),
    wrong_type: t('configTemplates.assign.statusWrongType', { type: template.device_type }),
  };

  const ownerName = anep ? t('configTemplates.anep') : (clients.find((c) => c.id === template.client_id)?.name ?? '');
  // What the footer sums up before the click.
  const targetCount = mode === 'all' ? (preview?.matched ?? 0) : readyCount;
  const skipped = result ? result.skipped_other_client + result.skipped_wrong_type + result.skipped_inactive + result.skipped_has_template : 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="config-template-modal tpc asg"
        data-testid="modal-assign-template"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div className="modal-title tpd-title">
            <span className="tpd-mark" aria-hidden="true">{template.device_type}</span>
            <div className="tpd-title-text">
              <h2>{t('configTemplates.assign.title')}</h2>
              <div className="tpd-title-sub">
                <span className="asg-template-name">{template.name}</span>
                {ownerName && <span className="asg-sep">·</span>}
                {ownerName && <span>{ownerName}</span>}
              </div>
            </div>
          </div>
          <button type="button" className="close-button" onClick={onClose} aria-label={t('common.close')}>&times;</button>
        </div>

        <div className="modal-content tpd-body asg-body">
          {error && <div className="error-message" role="alert">{error}</div>}

          {result ? (
            <section className="cu-section asg-result" data-testid="assign-result-summary">
              <div className="asg-result-head">
                <span className="asg-result-icon" aria-hidden="true">✓</span>
                <div>
                  <h3>{t('configTemplates.assign.resultTitle')}</h3>
                  <p>{t('configTemplates.assign.done', { assigned: formatNumber(result.assigned + result.already_assigned), name: clientName })}</p>
                </div>
              </div>
              <dl className="asg-facts">
                <div><dt>{t('configTemplates.assign.resultAssigned')}</dt><dd>{formatNumber(result.assigned)}</dd></div>
                <div><dt>{t('configTemplates.assign.resultAlready')}</dt><dd>{formatNumber(result.already_assigned)}</dd></div>
                {skipped > 0 && <div><dt>{t('configTemplates.assign.resultSkipped')}</dt><dd>{formatNumber(skipped)}</dd></div>}
                {clearOverrides && <div><dt>{t('configTemplates.assign.resultCleared')}</dt><dd>{formatNumber(result.overrides_cleared ?? 0)}</dd></div>}
                {(result.refresh_sent ?? 0) > 0 && <div data-testid="assign-result-refreshed"><dt>{t('configTemplates.assign.resultRefreshed')}</dt><dd>{formatNumber(result.refresh_sent ?? 0)}</dd></div>}
                {(result.refresh_failed ?? 0) > 0 && <div data-testid="assign-result-refresh-failed"><dt>{t('configTemplates.assign.resultRefreshFailed')}</dt><dd>{formatNumber(result.refresh_failed ?? 0)}</dd></div>}
              </dl>
            </section>
          ) : (
            <>
              <section className="cu-section">
                <header className="cu-section-head">
                  <h3>{t('configTemplates.assign.clientLabel')}</h3>
                  <p>{t('configTemplates.assign.clientIntro')}</p>
                </header>
                {anep ? (
                  <div className="cu-field">
                    <label htmlFor="assignClient">{t('configTemplates.assign.clientLabel')} *</label>
                    <select
                      id="assignClient"
                      data-testid="field-assign-client"
                      className="cu-input"
                      value={clientId}
                      onChange={(e) => setClientId(e.target.value)}
                      disabled={submitting}
                    >
                      <option value="">{t('configTemplates.ownerPickClient')}</option>
                      {clients.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="cu-client-card" data-testid="assign-client-fixed">
                    <span className="cu-client-label">{t('configTemplates.assign.clientLabel')}</span>
                    <strong>{clientName}</strong>
                  </div>
                )}
              </section>

              <section className="cu-section">
                <header className="cu-section-head">
                  <h3>{t('configTemplates.assign.scopeLabel')}</h3>
                  <p>{t('configTemplates.assign.scopeIntro', { type: template.device_type })}</p>
                </header>
                <div className="cu-role-grid" role="radiogroup" aria-label={t('configTemplates.assign.scopeLabel')}>
                  <label className={`cu-role${mode === 'all' ? ' is-selected' : ''}`} data-testid="assign-mode-all">
                    <input type="radio" name="assignMode" checked={mode === 'all'} onChange={() => setMode('all')} disabled={submitting} />
                    <span className="cu-role-name">{t('configTemplates.assign.modeAllTitle')}</span>
                    <span className="cu-role-desc">{t('configTemplates.assign.modeAllText', { type: template.device_type })}</span>
                  </label>
                  <label className={`cu-role${mode === 'some' ? ' is-selected' : ''}`} data-testid="assign-mode-some">
                    <input type="radio" name="assignMode" checked={mode === 'some'} onChange={() => setMode('some')} disabled={submitting} />
                    <span className="cu-role-name">{t('configTemplates.assign.modeSomeTitle')}</span>
                    <span className="cu-role-desc">{t('configTemplates.assign.modeSomeText')}</span>
                  </label>
                </div>

                {mode === 'all' && clientId && (
                  <div className="asg-preview" data-testid="assign-preview">
                    {previewing || !preview ? (
                      <span className="tpd-muted">{t('configTemplates.assign.previewLoading')}</span>
                    ) : preview.matched === 0 ? (
                      <span>{t('configTemplates.assign.previewNone', { type: template.device_type, name: clientName })}</span>
                    ) : (
                      <>
                        <strong>{t('configTemplates.assign.previewAll', { count: formatNumber(preview.matched), type: template.device_type, name: clientName })}</strong>
                        <span className="asg-preview-detail">
                          {t('configTemplates.assign.previewToAssign', { count: formatNumber(preview.assigned) })}
                          {preview.already_assigned > 0 && <> · {t('configTemplates.assign.previewAlready', { count: formatNumber(preview.already_assigned) })}</>}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </section>

              {mode === 'some' && (
                <section className="cu-section">
                  <header className="cu-section-head">
                    <h3>
                      {t('configTemplates.assign.devicesLabel')}
                      {deviceIds.length > 0 && <span className="ud-tab-count">{formatNumber(deviceIds.length)}</span>}
                    </h3>
                    <p>{t('configTemplates.assign.imeiIntro', { type: template.device_type })}</p>
                  </header>
                  <textarea
                    id="assignDevices"
                    data-testid="field-assign-devices"
                    rows={4}
                    value={deviceText}
                    onChange={(e) => setDeviceText(e.target.value)}
                    placeholder={t('configTemplates.assign.devicesPlaceholder')}
                    disabled={submitting || !clientId}
                    className="cu-input mono asg-textarea"
                    aria-label={t('configTemplates.assign.devicesLabel')}
                  />
                  {!clientId && <p className="cu-hint">{t('configTemplates.assign.pickClientFirst')}</p>}

                  {deviceIds.length > 0 && clientId && (
                    <div className="asg-check" data-testid="assign-check">
                      <div className="asg-check-head">
                        <span className={`asg-check-text${!checking && invalidIds.length > 0 ? ' is-warning' : ''}`}>
                          {checking
                            ? t('configTemplates.assign.checking')
                            : invalidIds.length === 0
                              ? t('configTemplates.assign.allOk', { count: formatNumber(deviceIds.length), name: clientName })
                              : t('configTemplates.assign.someInvalid', { count: formatNumber(invalidIds.length) })}
                        </span>
                        {!checking && invalidIds.length > 0 && (
                          <button type="button" className="tpl-link" onClick={removeInvalid} data-testid="btn-assign-remove-invalid">
                            {t('configTemplates.assign.removeInvalid')}
                          </button>
                        )}
                      </div>
                      <table className="tpd-table asg-table">
                        <thead>
                          <tr>
                            <th>{t('configTemplates.assign.devicesLabel')}</th>
                            <th>{t('configTemplates.assign.statusHeader')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deviceIds.map((id) => {
                            const status = statuses[id];
                            return (
                              <tr key={id} className={`asg-imei is-${status ?? 'pending'}`} data-testid={`assign-imei-${id}`}>
                                <td><code>{id}</code></td>
                                <td><span className="asg-status">{status ? statusLabel[status] : '…'}</span></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              )}

              <section className="cu-section cu-section-inline asg-options">
                <label className="cu-check">
                  <input type="checkbox" data-testid="field-assign-clear-overrides" checked={clearOverrides} onChange={(e) => setClearOverrides(e.target.checked)} disabled={submitting} />
                  <span>
                    <strong>{t('configTemplates.assign.clearOverrides')}</strong>
                    <span className="cu-hint asg-check-hint">{t('configTemplates.assign.clearOverridesHint')}</span>
                  </span>
                </label>
                <p className="cu-note">{t('configTemplates.assign.replaceNote')}</p>
              </section>
            </>
          )}
        </div>

        <div className="modal-actions tpd-footer tpc-footer">
          {result ? (
            <>
              <span />
              <div className="tpc-actions">
                <button type="button" className="btn btn-primary" onClick={onClose} data-testid="btn-assign-close">
                  {t('common.close')}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="tpc-summary" data-testid="assign-summary">
                {clientId ? (
                  <>
                    <strong>{clientName}</strong>
                    <span className="type-badge tpl-type">{template.device_type}</span>
                    <span className="tpc-summary-owner">{t('configTemplates.assign.summaryDevices', { count: formatNumber(targetCount) })}</span>
                  </>
                ) : (
                  <span className="tpd-muted">{t('configTemplates.assign.summaryEmpty')}</span>
                )}
              </div>
              <div className="tpc-actions">
                <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting} data-testid="btn-assign-cancel">
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  data-testid="btn-assign-submit"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                >
                  {submitting
                    ? t('configTemplates.assign.submitting')
                    : mode === 'all'
                      ? t('configTemplates.assign.submitAll')
                      : t('configTemplates.assign.submitSome', { count: formatNumber(readyCount) })}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignTemplateModal;
